import { isNotNullish } from '@airreading/common';
import { Dispatch, SetStateAction, useCallback, useEffect, useState } from 'react';

export function assert(condition: boolean, message: string): asserts condition {
  if (!condition) {
    throw new Error(message);
  }
}

export function assertNotNullish<T>(value: T | undefined | null, message?: string): asserts value is T {
  assert(isNotNullish(value), message || 'Value is nullish');
}

export type DebouncedAccessor<Data> = readonly [[liveData: Data, debouncedData: Data], Dispatch<SetStateAction<Data>>];
export type Setter<Data> = (data: Data) => void;

/**
 * Like `useState`, but with the restriction that this is meant to wrap around
 * some other existing, original state whose immediate change would trigger
 * some sort of request. This wrapper hook exposes both the live state and the
 * debounced state, as well as a replacement setter that updates the original
 * state for you on a debounced basis.
 * @param initOrDebouncedValue a state variable from the original state
 * @param debouncedSetter a setter function that updates the original state
 * @returns
 */
export function useDebouncedInput<Input = string>(
  initOrDebouncedValue: Input | undefined,
  debouncedSetter: (value: Input | undefined) => unknown,
  debounceDelay: number = 500,
): DebouncedAccessor<Input | undefined> {
  const [liveValue, setValue] = useState(initOrDebouncedValue);
  useEffect(() => {
    const timeoutId = setTimeout(() => {
      debouncedSetter(liveValue);
    }, debounceDelay);
    return () => clearTimeout(timeoutId);

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [liveValue]);

  return [[liveValue, initOrDebouncedValue], setValue];
}

export function isNotErrorString<T>(inputOrError: T | string): inputOrError is T {
  return typeof inputOrError !== 'string';
}

export function useFilterReset(...filterSetters: Array<Setter<undefined>>) {
  const resetFilter = useCallback(() => {
    filterSetters.forEach((setter) => setter(undefined));

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, filterSetters);

  return resetFilter;
}

export type DeployedEnvironment = 'local' | 'devqa' | 'staging' | 'production';
const environmentOverride = import.meta.env.VITE_OVERRIDE_DEPLOYED_ENVIRONMENT;

export function getEnvironmentLevel(): DeployedEnvironment {
  if (environmentOverride) {
    return environmentOverride;
  }

  const { hostname } = window.location;

  if (hostname === 'localhost') {
    return 'local';
  }

  if (hostname.endsWith('portal.airreading.com')) {
    return 'production';
  }

  if (hostname.endsWith('staging.airreadingclass.com')) {
    return 'staging';
  }

  if (hostname.endsWith('dev.qa.airreadingclass.com')) {
    return 'devqa';
  }

  // Support custom qa environments (qa0, etc.)
  if (hostname.match(/^qa\d+\.qa\.airreadingclass\.com$/) || hostname.endsWith('.qa.airreadingclass.com')) {
    return 'devqa';
  }

  // Support custom staging subdomains (if present)
  if (
    hostname.match(/^staging\d+\.staging\.airreadingclass\.com$/) ||
    hostname.endsWith('.staging.airreadingclass.com')
  ) {
    return 'staging';
  }

  console.warn(`Unknown hostname: ${hostname}, defaulting to staging for Sentry`);
  return 'staging';
}

export const formatCurrency = (value: number) => {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
  }).format(value);
};
